import { Collapse } from "@mui/material";
import type { TFormCreateBeneficiar } from "components/BeneficiaryFormContainer/types";
import type { TFormCreateTitle } from "components/TitleFormContainer/types";
import type { TPublicationData } from "containers/PublicationFormContainer";
import { ROLES } from "containers/ScopeContext/roles.enum";
import { useCompanySettings } from "hooks/useCompanySettings";
import { usePermissions } from "hooks/usePermissions";
import useUserRole from "hooks/useUserRole";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ICustomField } from "store/customFieldsStore";
import { useAppSelector } from "store/store";

import ButtonCollapse from "../ButtonCollapse";
import FormCustomField from "./FormCustomField";
import styles from "./style.module.scss";

interface ICustomFieldsCollapse {
    /**
     * The custom fields specific to the section.
     */
    sectionFields: ICustomField[];
    /**
     * Errors value from useForm to be able to display errors.
     */
    errors: any;
    /**
     * Mode of form
     */
    mode: "new" | "edit" | "edit-page";
}

// Collapse that reveals custom fields for each section.
const CustomFieldsCollapse = ({ sectionFields, errors, mode }: ICustomFieldsCollapse) => {
    const [t] = useTranslation();
    const { control, setValue, watch } = useFormContext<TFormCreateTitle | TFormCreateBeneficiar | TPublicationData>();

    const { hasPermissions } = usePermissions();

    const disableWithExceptions = (field: ICustomField | undefined) => {
        if (!field) return;
        if (field.name === "RIB") {
            return !hasPermissions(["beneficiary:edit-rib"]);
        }
        return false;
    };

    const { fields } = useFieldArray({
        control,
        name: "customFields",
    });

    // Used to set value of collapse on edit mode:
    const [isFirstRender, setIsFirstRender] = useState(true);

    // (Needed on edit mode) Open collapse only if there is at least one field that has a value entered:
    const areCFUsed = useMemo(() => {
        if (fields.length > 0) {
            const fieldsInSection = fields.filter((field: any) => {
                return sectionFields.some((item) => item.uuid === field.uuid);
            });
            return fieldsInSection.some((item: any) => item.value);
        } else {
            return false;
        }
    }, [fields, sectionFields]);

    const sectionHasFields = sectionFields.length > 0;
    const [showCollapse, setShowChollapse] = useState(areCFUsed);

    useEffect(() => {
        if (mode === "edit-page" && isFirstRender) {
            if (sectionHasFields && areCFUsed) {
                setShowChollapse(true);
                setIsFirstRender(false);
            } else {
                setShowChollapse(false);
                setIsFirstRender(false);
            }
        } else {
            // If there is at least one mandatory customField, open collapse: if fields not rendered the errors won't bounce back on form.
            const mandatoryFieldsExist = sectionFields.some((item) => item.isRequired);
            if (mandatoryFieldsExist) setShowChollapse(true);
        }
    }, [mode, sectionHasFields, areCFUsed, isFirstRender]);

    return (
        <div className={styles["custom-fields-collapse"]}>
            {sectionFields.length > 0 && (
                <>
                    <ButtonCollapse
                        collapse={showCollapse}
                        onCollapse={() => setShowChollapse((prev) => !prev)}
                        btnText={t("CustomFields.CustomFields")}
                    />
                    <Collapse in={showCollapse} timeout={500} unmountOnExit>
                        <div className={styles["fields-container"]}>
                            {fields?.map((value: any, index: number) => {
                                const field = sectionFields.find((item) => item.uuid === value.uuid);
                                const disabled = disableWithExceptions(field);
                                return (
                                    <FormCustomField
                                        key={`field.${field?.uuid}.${index}`}
                                        formData={{ control, errors, watch, setValue }}
                                        field={field}
                                        index={index}
                                        disabled={disabled}
                                    />
                                );
                            })}
                        </div>
                    </Collapse>
                </>
            )}
        </div>
    );
};

export default CustomFieldsCollapse;
